import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Feather, Play } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SoulHero from "@/components/soul/SoulHero";
import SoulBreadcrumb from "@/components/soul/SoulBreadcrumb";
import N8Emblem from "@/components/N8Emblem";
import ChipSelect from "@/components/soul/nervous/ChipSelect";
import WaysToReturn from "@/components/soul/nervous/WaysToReturn";
import MySignals from "@/components/soul/nervous/MySignals";
import { endpoints } from "@/lib/api";
import { HERO } from "@/lib/heroImages";
import {
  CHECKIN_OPTIONS,
  LOCATION_OPTIONS,
  CONTEXT_OPTIONS,
  RESPONSE_OPTIONS,
  REACH_FOR_OPTIONS,
  SUPPORT_OPTIONS,
  RESPOND_OPTIONS,
  CONNECTION_OPTIONS,
  COMPLETION_OPTIONS,
  STAGES,
} from "@/pages/soul/nervous/nervousContent";

/**
 * NervousSystem — the main page.
 *
 * Three surfaces on one page:
 *   1. Hero + entry point ("Begin the journey")
 *   2. Ways to Return — grounding toolkit
 *   3. My Signals — user's own patterns
 *
 * When the user clicks Begin, we enter session mode. Session mode
 * scrolls through the five stages plus a completion panel. Every prior
 * stage stays visible so the experience feels like a guided journey
 * rather than a wizard.
 */
export default function NervousSystem() {
  const nav = useNavigate();
  const [session, setSession] = useState(false);
  const [stageIndex, setStageIndex] = useState(0);
  const [state, setState] = useState(initialState());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [signalsRefresh, setSignalsRefresh] = useState(0);

  const startSession = () => {
    setState(initialState());
    setStageIndex(0);
    setSession(true);
    setSaved(false);
    setTimeout(() => {
      document
        .getElementById("ns-stage-notice")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const goToStage = (idx) => {
    setStageIndex(idx);
    setTimeout(() => {
      const target =
        idx < STAGES.length
          ? STAGES[idx].key
          : "close";
      document
        .getElementById(`ns-stage-${target}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const canContinue = useMemo(() => {
    switch (stageIndex) {
      case 0:
        return state.notice.sensations.length > 0;
      case 1:
        return state.recognize.length > 0;
      case 2:
        return state.understand.length > 0;
      case 3:
        return state.discover.supports.length > 0;
      case 4:
        return !!state.respond.choice;
      default:
        return false;
    }
  }, [stageIndex, state]);

  const closeSession = () => {
    setSession(false);
    setStageIndex(0);
    setState(initialState());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const composeJournal = () => {
    const parts = [];
    parts.push("Soul · Nervous System · Session");
    parts.push("");
    parts.push("Notice:");
    parts.push(`  Signals — ${state.notice.sensations.join(", ") || "—"}`);
    if (state.notice.locations.length)
      parts.push(`  Where — ${state.notice.locations.join(", ")}`);
    if (state.notice.freetext?.trim())
      parts.push(`  Free note — ${state.notice.freetext.trim()}`);
    parts.push("");
    parts.push(`Recognize: ${state.recognize.join(", ") || "—"}`);
    parts.push(`Understand: ${state.understand.join(", ") || "—"}`);
    parts.push("");
    parts.push("Discover:");
    if (state.discover.reached_for.length)
      parts.push(`  Reach for — ${state.discover.reached_for.join(", ")}`);
    if (state.discover.connection)
      parts.push(`  Afterward feel — ${state.discover.connection}`);
    parts.push(`  Actually supports — ${state.discover.supports.join(", ") || "—"}`);
    parts.push("");
    parts.push(`Respond: ${state.respond.choice || "—"}`);
    if (state.respond.meeting_myself?.trim())
      parts.push(`  Meeting myself — ${state.respond.meeting_myself.trim()}`);
    parts.push("");
    if (state.completion.feeling)
      parts.push(
        `Completion — ${
          (COMPLETION_OPTIONS.find((o) => o.key === state.completion.feeling) || {})
            .label || state.completion.feeling
        }`,
      );
    if (state.completion.remember?.trim()) {
      parts.push("What I want to remember:");
      parts.push(state.completion.remember.trim());
    }
    return parts.join("\n");
  };

  const saveSession = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // 1. Structured entry powers My Signals.
      await endpoints.nsSaveEntry({
        notice: state.notice,
        recognize: state.recognize,
        understand: state.understand,
        discover: state.discover,
        respond: state.respond,
        completion: state.completion,
      });
      // 2. Long-form Journal entry the user can revisit any time.
      await endpoints.createJournal({
        prompt: "Soul · Nervous System · Session",
        content: composeJournal(),
        mood_tag: "reflect",
        entry_type: "Soul Work Reflection",
      });
      setSaved(true);
      setSignalsRefresh((n) => n + 1);
      toast.success("Held in your Journal.", {
        action: { label: "Open Journal", onClick: () => nav("/journal") },
      });
    } catch {
      toast.error("Couldn't save just yet — try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-16" data-testid="ns-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SoulBreadcrumb
          items={[
            { label: "Rituals", to: "/rituals" },
            { label: "Soul", to: "/soul" },
            { label: "Regulation & Presence", to: "/soul/regulation-presence" },
            { label: "Nervous System" },
          ]}
        />
        <button
          type="button"
          onClick={() => nav("/soul/regulation-presence")}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))] transition-colors"
          data-testid="ns-back"
        >
          <ArrowLeft size={12} /> Back to Regulation & Presence
        </button>
      </div>

      <SoulHero
        emblem=""
        eyebrow="Nervous System"
        title="Learn the language of your body."
        supporting="Your body often notices before your mind has words for what you're experiencing."
        body={"Tension. Restlessness. Heaviness. Warmth. A racing mind. A desire to move. A desire to withdraw. A sense of ease. Your body is communicating with you all the time. This space isn't about labeling every sensation or forcing yourself to feel calm. It's about becoming curious enough to notice your signals, recognize your patterns, and learn what supports you."}
        image={HERO.nervousSystem}
        testid="ns-hero"
      />

      <section
        className="reveal reveal-1 max-w-3xl"
        data-testid="ns-invitation"
      >
        <div className="mb-6 flex items-center gap-3" data-testid="ns-emblem-wrap">
          <N8Emblem
            mode="simplified"
            size={32}
            ariaLabel="N8ONE emblem"
            testid="ns-emblem"
          />
          <span className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--copper))]">
            Nervous System Practice
          </span>
        </div>
        <p className="font-display italic text-2xl md:text-3xl leading-snug text-[hsl(var(--clay))]">
          You don't have to immediately change what you feel. Begin by noticing what is here.
        </p>
        {!session && (
          <Button
            type="button"
            onClick={startSession}
            data-testid="ns-begin"
            className="mt-8 rounded-full bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-[hsl(var(--paper))] px-7"
          >
            <Play size={14} className="mr-1.5" /> Begin the journey
          </Button>
        )}
      </section>

      {/* Session */}
      {session && (
        <>
          <StageRail current={stageIndex} />

          {/* Stage I — Notice (also the opening check-in) */}
          <Stage
            id="ns-stage-notice"
            active={stageIndex >= 0}
            visible={stageIndex >= 0}
            numeral={STAGES[0].numeral}
            title={STAGES[0].title}
            subtitle={STAGES[0].subtitle}
            principle={STAGES[0].principle}
            testid="ns-stage-notice"
          >
            <div className="max-w-3xl">
              <p className="text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85">
                Before trying to understand it, simply notice. You are not diagnosing anything — you are
                paying attention.
              </p>
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                  What are you noticing?
                </div>
                <div className="mt-3">
                  <ChipSelect
                    options={CHECKIN_OPTIONS}
                    value={state.notice.sensations}
                    onChange={(v) => setNotice({ sensations: v }, state, setState)}
                    testidPrefix="ns-checkin"
                  />
                </div>
              </div>
              <div className="mt-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                  Where in the body — optional
                </div>
                <div className="mt-3">
                  <ChipSelect
                    options={LOCATION_OPTIONS}
                    value={state.notice.locations}
                    onChange={(v) => setNotice({ locations: v }, state, setState)}
                    allowOther={false}
                    testidPrefix="ns-loc"
                  />
                </div>
              </div>
              <div className="mt-8">
                <label
                  htmlFor="ns-freetext"
                  className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]"
                >
                  In your own words — optional
                </label>
                <Textarea
                  id="ns-freetext"
                  data-testid="ns-freetext"
                  value={state.notice.freetext}
                  onChange={(e) =>
                    setNotice({ freetext: e.target.value }, state, setState)
                  }
                  rows={3}
                  placeholder="A short sentence is enough. You are only noticing."
                  className="mt-3 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
                />
              </div>
            </div>
            {stageIndex === 0 && (
              <ContinueBar
                canContinue={canContinue}
                onNext={() => goToStage(1)}
                summary={
                  state.notice.sensations[0]
                    ? `You're noticing ${state.notice.sensations.slice(0, 3).join(", ").toLowerCase()}.`
                    : null
                }
              />
            )}
          </Stage>

          {/* Stage II — Recognize */}
          {stageIndex >= 1 && (
            <Stage
              id="ns-stage-recognize"
              numeral={STAGES[1].numeral}
              title={STAGES[1].title}
              subtitle={STAGES[1].subtitle}
              principle={STAGES[1].principle}
              testid="ns-stage-recognize"
            >
              <div className="max-w-3xl">
                <p className="text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85">
                  When have you noticed something similar? Include both the difficult moments and the
                  bright ones — your body responds to more than distress.
                </p>
                <div className="mt-6">
                  <ChipSelect
                    options={CONTEXT_OPTIONS}
                    value={state.recognize}
                    onChange={(v) => setState((s) => ({ ...s, recognize: v }))}
                    testidPrefix="ns-context"
                  />
                </div>
              </div>
              {stageIndex === 1 && (
                <ContinueBar canContinue={canContinue} onNext={() => goToStage(2)} />
              )}
            </Stage>
          )}

          {/* Stage III — Understand */}
          {stageIndex >= 2 && (
            <Stage
              id="ns-stage-understand"
              numeral={STAGES[2].numeral}
              title={STAGES[2].title}
              subtitle={STAGES[2].subtitle}
              principle={STAGES[2].principle}
              testid="ns-stage-understand"
            >
              <div className="max-w-3xl">
                <p className="text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85">
                  When your body signals like this, what tends to happen next? Not good, not bad. Just
                  what you tend to do.
                </p>
                <div className="mt-6">
                  <ChipSelect
                    options={RESPONSE_OPTIONS}
                    value={state.understand}
                    onChange={(v) => setState((s) => ({ ...s, understand: v }))}
                    testidPrefix="ns-response"
                  />
                </div>
              </div>
              {stageIndex === 2 && (
                <ContinueBar canContinue={canContinue} onNext={() => goToStage(3)} />
              )}
            </Stage>
          )}

          {/* Stage IV — Discover */}
          {stageIndex >= 3 && (
            <Stage
              id="ns-stage-discover"
              numeral={STAGES[3].numeral}
              title={STAGES[3].title}
              subtitle={STAGES[3].subtitle}
              principle={STAGES[3].principle}
              testid="ns-stage-discover"
            >
              <div className="max-w-3xl space-y-8">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                    When I feel this way, I usually reach for — optional
                  </div>
                  <div className="mt-3">
                    <ChipSelect
                      options={REACH_FOR_OPTIONS}
                      value={state.discover.reached_for}
                      onChange={(v) =>
                        setDiscover({ reached_for: v }, state, setState)
                      }
                      testidPrefix="ns-reach"
                    />
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                    Afterward, I generally feel — optional
                  </div>
                  <div className="mt-3">
                    <ChipSelect
                      options={CONNECTION_OPTIONS}
                      value={state.discover.connection}
                      onChange={(v) => setDiscover({ connection: v }, state, setState)}
                      mode="single"
                      allowOther={false}
                      testidPrefix="ns-connection"
                    />
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                    What actually supports me
                  </div>
                  <div className="mt-3">
                    <ChipSelect
                      options={SUPPORT_OPTIONS}
                      value={state.discover.supports}
                      onChange={(v) => setDiscover({ supports: v }, state, setState)}
                      testidPrefix="ns-support"
                    />
                  </div>
                </div>
              </div>
              {stageIndex === 3 && (
                <ContinueBar canContinue={canContinue} onNext={() => goToStage(4)} />
              )}
            </Stage>
          )}

          {/* Stage V — Respond */}
          {stageIndex >= 4 && (
            <Stage
              id="ns-stage-respond"
              numeral={STAGES[4].numeral}
              title={STAGES[4].title}
              subtitle={STAGES[4].subtitle}
              principle={STAGES[4].principle}
              testid="ns-stage-respond"
            >
              <div className="max-w-3xl space-y-8">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                    Choose one small response
                  </div>
                  <div className="mt-3">
                    <ChipSelect
                      options={RESPOND_OPTIONS}
                      value={state.respond.choice}
                      onChange={(v) => setRespond({ choice: v }, state, setState)}
                      mode="single"
                      testidPrefix="ns-respond"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="ns-meeting-myself"
                    className="font-display italic text-lg md:text-xl text-[hsl(var(--clay))] leading-snug"
                  >
                    What would meeting myself where I am look like right now?
                  </label>
                  <Textarea
                    id="ns-meeting-myself"
                    data-testid="ns-meeting-myself"
                    value={state.respond.meeting_myself}
                    onChange={(e) =>
                      setRespond({ meeting_myself: e.target.value }, state, setState)
                    }
                    rows={3}
                    placeholder="A sentence, in your own words. Optional."
                    className="mt-3 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
                  />
                </div>
              </div>
              {stageIndex === 4 && (
                <ContinueBar
                  canContinue={canContinue}
                  onNext={() => goToStage(5)}
                  label="Complete"
                />
              )}
            </Stage>
          )}

          {/* Completion */}
          {stageIndex >= 5 && (
            <section
              id="ns-stage-close"
              className="reveal reveal-1 relative overflow-hidden rounded-[24px] border hairline bg-gradient-to-br from-[hsl(var(--sand))]/50 to-[hsl(var(--paper))] p-8 md:p-14"
              data-testid="ns-stage-close"
            >
              <div className="max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
                    What did you notice?
                  </div>
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    <ChipSelect
                      options={COMPLETION_OPTIONS}
                      value={state.completion.feeling}
                      onChange={(v) =>
                        setCompletion({ feeling: v }, state, setState)
                      }
                      mode="single"
                      allowOther={false}
                      testidPrefix="ns-completion"
                    />
                  </div>
                </div>
                <div className="mt-10">
                  <label
                    htmlFor="ns-remember"
                    className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]"
                  >
                    What do you want to remember from this moment? — optional
                  </label>
                  <Textarea
                    id="ns-remember"
                    data-testid="ns-remember"
                    value={state.completion.remember}
                    onChange={(e) =>
                      setCompletion({ remember: e.target.value }, state, setState)
                    }
                    rows={4}
                    placeholder="One sentence, in your own words."
                    className="mt-3 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
                  />
                </div>

                <div className="mt-8 flex items-center gap-3 flex-wrap justify-center">
                  <Button
                    type="button"
                    onClick={saveSession}
                    disabled={saving || saved}
                    data-testid="ns-save"
                    className="rounded-full bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-[hsl(var(--paper))]"
                  >
                    {saved ? (
                      <>
                        <Check size={14} className="mr-1.5" /> Held in Journal
                      </>
                    ) : (
                      <>
                        <Feather size={14} className="mr-1.5" />
                        {saving ? "Holding…" : "Save to Journal"}
                      </>
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={closeSession}
                    className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))]"
                    data-testid="ns-finish"
                  >
                    Return to Nervous System
                  </button>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Ways to Return — always visible below the primary journey */}
      <WaysToReturn />

      {/* My Signals — reflects the user's own recorded patterns */}
      <MySignals refreshKey={signalsRefresh} />

      {/* Medical safety note — quiet but always present */}
      <section
        className="p-6 md:p-8 rounded-[20px] border hairline bg-[hsl(var(--sand))]/40 max-w-3xl"
        data-testid="ns-medical-note"
      >
        <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
          A gentle note
        </div>
        <p className="mt-3 text-[14.5px] leading-relaxed text-[hsl(var(--fg))]/80">
          Physical sensations can have many causes. New, severe, or persistent symptoms deserve care
          from a qualified healthcare professional. Nothing here treats or replaces medical or
          mental-health care.
        </p>
      </section>
    </div>
  );
}

// ------------------- helpers -------------------

function initialState() {
  return {
    notice: { sensations: [], locations: [], freetext: "" },
    recognize: [],
    understand: [],
    discover: { reached_for: [], connection: null, supports: [] },
    respond: { choice: null, meeting_myself: "" },
    completion: { feeling: null, remember: "" },
  };
}
function setNotice(patch, state, setState) {
  setState({ ...state, notice: { ...state.notice, ...patch } });
}
function setDiscover(patch, state, setState) {
  setState({ ...state, discover: { ...state.discover, ...patch } });
}
function setRespond(patch, state, setState) {
  setState({ ...state, respond: { ...state.respond, ...patch } });
}
function setCompletion(patch, state, setState) {
  setState({ ...state, completion: { ...state.completion, ...patch } });
}

function StageRail({ current }) {
  return (
    <nav
      aria-label="Nervous System progress"
      data-testid="ns-stage-rail"
      className="sticky top-16 z-10 -mx-2 md:mx-0 py-3 px-2 md:px-0"
    >
      <ol className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.24em]">
        {STAGES.map((s, i) => (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={`tabular-nums ${
                i === current
                  ? "text-[hsl(var(--copper))]"
                  : i < current
                    ? "text-[hsl(var(--fg))]/60"
                    : "text-[hsl(var(--muted-foreground))]/50"
              }`}
            >
              {s.numeral}
            </span>
            <span
              className={
                i === current
                  ? "text-[hsl(var(--fg))]"
                  : i < current
                    ? "text-[hsl(var(--fg))]/60"
                    : "text-[hsl(var(--muted-foreground))]/60"
              }
            >
              {s.title}
            </span>
            {i < STAGES.length - 1 && (
              <span aria-hidden className="w-6 h-px bg-[hsl(var(--line))]" />
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Stage({
  id,
  numeral,
  title,
  subtitle,
  principle,
  children,
  testid,
}) {
  return (
    <section id={id} data-testid={testid} className="reveal reveal-1">
      <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] inline-flex items-center gap-3">
        <span className="tabular-nums">{numeral}</span>
        <span aria-hidden className="w-8 h-px bg-[hsl(var(--copper))]/40" />
        <span>{title}</span>
      </div>
      <h2 className="font-display text-3xl md:text-4xl mt-4 tracking-tight italic max-w-3xl">
        {subtitle}
      </h2>
      {principle && (
        <p className="mt-3 text-[13px] text-[hsl(var(--muted-foreground))] max-w-2xl italic">
          {principle}
        </p>
      )}
      <div className="mt-8">{children}</div>
    </section>
  );
}

function ContinueBar({ canContinue, onNext, label = "Continue", summary }) {
  return (
    <div className="mt-10 flex items-center gap-4 flex-wrap">
      <Button
        type="button"
        onClick={onNext}
        disabled={!canContinue}
        data-testid="ns-stage-continue"
        className="rounded-full bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-[hsl(var(--paper))]"
      >
        {label}
      </Button>
      {summary && (
        <span className="text-sm text-[hsl(var(--muted-foreground))] italic">
          {summary}
        </span>
      )}
    </div>
  );
}
